<template>
  <div class="chatbot-window" :class="{ expanded: isChatExpanded }">
    <!-- Close Button -->
    <!-- Greeting Message -->
    <div class="welcome-message">
      <div class="avatar-and-greeting">
        <SwdAvatar class="avatar" />
        <div v-if="userProfile" class="greeting-text">
          <p>Hi, {{ userProfile.firstName }}!</p>
        </div>
        <div v-else>
          <p>Loading...</p>
        </div>
        <button class="close-button" @click="$emit('close')">✖</button>
      </div>
    </div>

    <!-- Tab Content Area -->
    <div class="tab-content">
      <FaqWindow v-if="selectedTab === 'FAQ'" />
      <HelpWindow v-if="selectedTab === 'HELP'" />
      <div v-if="selectedTab === 'ASK MATTHEW'" class="tab-panel">
        <div class="ask-mathew-content">
          <div class="ask-matthew-header">
            <h2>AskMatthew</h2>
            <button class="toggle-button" @click="toggleChatHistory"><InlineSvg :src="IconExpand" /></button>
          </div>
          <div class="chat-history" :class="{ expanded: isChatExpanded }">
            <div
              v-for="(msg, index) in chatHistory"
              :key="index"
              :class="['chat-item', msg.sender === 'bot' ? 'chat-bot' : 'chat-user']"
            >
              <!-- Message Content -->
              <div class="message-container" :class="{ expanded: isChatExpanded }">
                <div v-if="msg.sender === 'bot'" class="response-container">
                  <button v-if="msg.sender === 'bot'" class="copy-button" @click="handleCopy(msg.text, index)">
                    <InlineSvg :src="IconCopy" class="copyicon" />
                    <span v-if="copiedIndex === index">Copied!</span>
                    <span v-else>Copy</span>
                  </button>
                  <div v-html="formatMessage(msg.text)"></div>
                </div>
                <div v-else class="user-message">
                  {{ msg.text }}
                </div>
              </div>

              <!-- Timestamp -->
              <div class="timestamp">{{ msg.timestamp }}</div>

              <!-- Actions (Like and More Options) for Bot Messages -->
              <div v-if="msg.sender === 'bot'" class="response-actions">
                <InlineSvg :src="IconLike" class="like-icon" @click="handleLike(index)" />
                <span class="more-options" @click="toggleFeedbackForm(index)">...</span>
              </div>

              <!-- Feedback Form -->
              <div v-if="feedbackIndex === index" class="feedback-form">
                <p>Thank you for the chat! Your review helps me grow!</p>
                <p>How would you rate your experience with our products?</p>
                <div class="feedback-options">
                  <span>
                    <label>
                      <input v-model="msg.feedback" type="radio" :name="'feedback-' + index" value="Excellent" />
                      Excellent
                    </label>
                  </span>
                  <span>
                    <label>
                      <input v-model="msg.feedback" type="radio" :name="'feedback-' + index" value="Good" />
                      Good
                    </label>
                  </span>
                  <span>
                    <label>
                      <input v-model="msg.feedback" type="radio" :name="'feedback-' + index" value="Could Be Better" />
                      Could Be Better
                    </label>
                  </span>
                  <span>
                    <label>
                      <input v-model="msg.feedback" type="radio" :name="'feedback-' + index" value="A Lot to Improve" />
                      A Lot to Improve
                    </label>
                  </span>
                  <button class="submit-feedback" @click="submitFeedback(index)">Submit</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <form class="chat-form" @submit.prevent="connectAndSend">
          <textarea id="prompt" v-model="prompt" placeholder="Type your message..." required></textarea>
          <div>
            <button v-if="!isConnecting" :class="{ 'small-button': true }" type="submit">Send</button>
            <div v-else id="spinner" class="spinner"></div>
          </div>
        </form>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tabs">
      <button :class="{ active: selectedTab === 'FAQ' }" class="tab-button" @click="selectedTab = 'FAQ'">
        <InlineSvg :src="IconFaq" class="icon" />FAQ
      </button>
      <button :class="{ active: selectedTab === 'HELP' }" class="tab-button" @click="selectedTab = 'HELP'">
        <InlineSvg :src="IconHelp" class="icon" />Help
      </button>
      <button
        :class="{ active: selectedTab === 'ASK MATTHEW' }"
        class="tab-button"
        @click="selectedTab = 'ASK MATTHEW'"
      >
        <InlineSvg :src="IconAskMathew" class="icon" />AskMatthew
      </button>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import FaqWindow from '@/components/Global/FaqWindow.vue'
import IconFaq from '@/assets/svg/icon-faq.svg'
import IconHelp from '@/assets/svg/icon-help.svg'
import IconAskMathew from '@/assets/svg/icon-ask-mathew.svg'
import IconCopy from '@/assets/svg/icon-copy.svg'
import IconLike from '@/assets/svg/icon-like.svg'
import IconExpand from '@/assets/svg/icon-expand.svg'
import HelpWindow from '@/components/Global/HelpWindow.vue'

export default {
  components: {
    FaqWindow,
    HelpWindow,
  },
  props: {
    userProfile: {
      type: Object,
      required: true,
    },
  },
  emits: ['close'],
  setup(props) {
    const prompt = ref('')
    const isConnecting = ref(false)
    const selectedTab = ref('ASK MATTHEW')
    const chatHistory = ref([])
    let socket = null
    const copiedIndex = ref(null)
    const isChatExpanded = ref(false)
    const feedbackIndex = ref(null)

    const handleLike = (index) => {
      console.log(`Like clicked for message at index ${index}`)
    }

    const toggleChatHistory = () => {
      isChatExpanded.value = !isChatExpanded.value
    }
    const toggleFeedbackForm = (index) => {
      if (feedbackIndex.value === index) {
        feedbackIndex.value = null // Hide the feedback form
      } else {
        feedbackIndex.value = index // Show the feedback form for the clicked message
      }
    }
    const connectAndSend = () => {
      if (!prompt.value || isConnecting.value) return // Prevent sending if already waiting for a response

      const userId = props.userProfile.firstName
      const wsUrl = `wss://rh8gj8m97k.execute-api.us-east-1.amazonaws.com/prod/?userId=${userId}`

      if (!socket || socket.readyState !== WebSocket.OPEN) {
        isConnecting.value = true
        socket = new WebSocket(wsUrl)

        socket.onopen = () => {
          console.log('WebSocket connection established.')
          sendMessage()
        }

        socket.onmessage = (event) => {
          console.log('Message received:', event.data)
          const formattedMessage = formatMessage(event.data)
          chatHistory.value.push({
            sender: 'bot',
            text: formattedMessage,
            timestamp: getFormattedTime(),
            showFeedbackForm: true,
          })
          isConnecting.value = false // Allow sending a new message
        }

        socket.onerror = () => {
          console.error('WebSocket error occurred.')
          isConnecting.value = false // Reset the state in case of error
        }

        socket.onclose = () => {
          console.log('WebSocket connection closed.')
          isConnecting.value = false // Reset the state if connection is closed
        }
      } else {
        sendMessage()
      }
    }

    const sendMessage = () => {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        console.error('WebSocket is not connected.')
        return
      }

      isConnecting.value = true // Block user from sending a new message
      socket.send(JSON.stringify({ userId: props.userProfile.firstName, action: 'get_reply', prompt: prompt.value }))
      chatHistory.value.push({
        sender: 'user',
        text: prompt.value,
        timestamp: getFormattedTime(),
      })

      prompt.value = '' // Clear input field
    }

    const getFormattedTime = () => {
      const now = new Date()
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    }

    function formatPlainText(message) {
      let parsedMessage

      // Parse JSON if needed
      try {
        const messageObj = JSON.parse(message)
        parsedMessage = messageObj.message || message
      } catch (e) {
        parsedMessage = message
      }

      const plainTextMessage = parsedMessage
        .replace(/<\/?[^>]+(>|$)/g, '') // Remove HTML tags
        .replace(/\\\$/g, '$') // Replace escaped dollar signs
        .replace(/\\n/g, '\n') // Replace escaped newlines with actual newlines
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold formatting
        .replace(/^\s*-\s+/gm, '') // Remove hyphens for list items
        .split('\n')
        .map((line) => line.trim()) // Trim each line
        .filter((line) => line.length > 0) // Remove empty lines
        .join('\n') // Join lines with actual newlines

      return plainTextMessage
    }

    function formatMessage(message) {
      let parsedMessage

      // Parse JSON if needed
      try {
        const messageObj = JSON.parse(message)
        parsedMessage = messageObj.message || message
      } catch (e) {
        parsedMessage = message
      }
      const formattedMessage = parsedMessage
        .replace(/\\\$/g, '$') // Replace escaped dollar signs
        .replace(/\\n/g, '<br>') // Convert escaped newlines to <br> for UI rendering
        .replace(/^\s*-\s+/gm, '<li>') // Detect and format list items starting with a hyphen
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Convert **bold** text
        .split('\n')
        .map((line) => line.trim()) // Trim each line
        .filter((line) => line.length > 0) // Remove empty lines
        .map((line) => {
          if (line.startsWith('<li>')) {
            return `${line}</li>` // Close list items
          } else {
            return `<p>${line}</p>` // Wrap non-list items in paragraphs
          }
        })
        .join('')
      return `<ul>${formattedMessage}</ul>`
    }

    const handleCopy = (text, index) => {
      const plainText = formatPlainText(text)

      navigator.clipboard
        .writeText(plainText)
        .then(() => {
          copiedIndex.value = index // Set the copied index
          setTimeout(() => {
            copiedIndex.value = null // Reset after 3 seconds
          }, 3000) // Set for 3 seconds
        })
        .catch((err) => {
          console.error('Failed to copy text:', err)
        })
    }

    return {
      prompt,
      isConnecting,
      selectedTab,
      chatHistory,
      connectAndSend,
      isChatExpanded,
      toggleChatHistory,
      feedbackIndex,
      formatMessage,
      copiedIndex,
      handleCopy,
      IconFaq,
      IconHelp,
      IconAskMathew,
      IconCopy,
      IconLike,
      IconExpand,
      toggleFeedbackForm,
      handleLike,
    }
  },
}
</script>

<style scoped>
.chatbot-window {
  position: fixed;
  bottom: 5px;
  right: 5px;
  width: 50%;
  height: 510px;
  background-color: #fff;
  border: 1px solid #ddd;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  padding: 10px;
  overflow-y: auto;
  z-index: 69;
}

.chatbot-window.expanded {
  width: 80%;
  height: 100%;
}

.message-container.expanded {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 925px;
}

.ask-matthew-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f0eaff;
  padding: 5px;
  padding-left: 10px;
}

.ask-matthew-header h2 {
  margin: 0;
}

.toggle-button {
  margin-left: 10px;
}

.chatbot-window .ask-matthew-header button {
  background-color: #e5e7eb;
}

.chatbot-window button {
  padding: 5px 10px;
  background-color: #073763;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.chatbot-window :deep(ul) {
  list-style-type: disc !important;
  font-size: 14px;
}

.chatbot-window :deep(li) {
  margin-bottom: 8px;
  color: #2980b9;
  margin-left: 20px;
}

.chatbot-window :deep(strong) {
  font-weight: bold;
  color: #c0392b;
}

.chatbot-window :deep(p) {
  font-size: 14px;
  color: #555;
}

.timestamp {
  font-size: 0.75rem;
  color: gray;
  margin-top: 0.2rem;
  text-align: right;
  padding-right: 5px;
}

.avatar-and-greeting button {
  align-self: flex-end !important;
  cursor: pointer;
  background: #073763;
  color: #f1f1f1;
  border: none;
  font-size: 10px;
}

.feedback-form .submit-feedback {
  display: inline-block;
  margin: 10px auto 0;
  padding: 5px 15px;
  border: none;
  background-color: #073763;
  color: white;
  cursor: pointer;
}

.feedback-options span {
  display: block;
  font-size: 14px;
  color: #555;
}

.faq-content div {
  display: block;
  margin-bottom: 8px;
}

.feedback-options input {
  margin-right: 8px;
}

.welcome-message {
  position: relative;
  padding: 10px;
  font-size: 10px;
}

.avatar-and-greeting {
  display: flex;
  align-items: center;
  gap: 6px;
}

.close-button {
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  font-size: 10px;
  cursor: pointer;
}

.chat-history .copy-button {
  float: right;
  cursor: pointer;
  background-color: #f1f0f0;
  color: #000;
  border: none;
  font-size: 10px;
}

.avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 10px;
}

.greeting-text p {
  margin: 0;
  line-height: 1.5;
}

.greeting-text p:first-child {
  font-weight: bold;
}

.chat-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  position: relative;
  max-width: 70%;
}

.chat-bot {
  align-self: flex-start;
}

.chat-user {
  align-self: flex-start;
  font-weight: bold;
  border-radius: 10px;
}

.message-container {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 550px;
}

.user-message {
  background-color: #d9ecff;
  padding: 10px;
  border-radius: 10px;
  align-self: flex-start;
}

.response-container {
  background-color: #f0eaff;
  padding: 10px;
  border-radius: 10px;
  align-self: flex-start;
  overflow-y: auto;
}

.response-actions {
  display: flex;
  gap: 10px;
  align-self: flex-end;
}

.like-icon {
  cursor: pointer;
  width: 40px;
  height: 30px;
}

.more-options {
  cursor: pointer;
  background: none;
  border: none;
  font-size: 18px;
  color: #555;
}

.feedback-form {
  margin-top: 10px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 10px;
  background-color: #f1f0f0;
  max-width: 100%;
  position: relative;
}

.tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.ask-mathew-content {
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.ask-mathew-content h2 {
  color: #073763;
  font-size: 16px;
  margin-bottom: 10px;
  font-weight: bold;
}

.ask-mathew-content.fullscreen {
  width: auto;
  height: auto;
  position: fixed;
  top: 0;
  left: 0;
  background-color: white;
  overflow-y: auto;
}

.chat-history {
  display: flex;
  flex-direction: column;
  height: 350px;
  gap: 5px;
  padding: 5px;
  overflow-y: auto;
}

.chat-history.expanded {
  height: 450px;
}

.chat-bubble {
  max-width: 60%;
  padding: 10px;
  border-radius: 10px;
  font-size: 14px;
  word-wrap: break-word;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chat-bubble.user {
  align-self: flex-end;
  background-color: #f1f1f1;
  color: #000;
  width: 300px;
  border-top-left-radius: 0;
}

.chat-bubble.bot {
  background-color: #c0c4cc;
  color: #000;
  width: 400px;
  border-top-right-radius: 0;
}

.chat-form {
  display: flex;
  gap: 8px;
  padding: 16px;
  background-color: white;
  border-top: 1px solid #ddd;
  align-items: center;
}

.chat-form textarea {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px;
  font-size: 14px;
  resize: none;
}

.chat-form button {
  background-color: #073763;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  min-width: 70px;
  text-align: center;
}

.chat-form button.connecting {
  background-color: #ddd;
  cursor: not-allowed;
}

.chat-form button:disabled {
  background-color: #ddd;
  color: #999;
}

.chat-form button:hover:not(:disabled) {
  background-color: #0656a3;
}

.tabs {
  display: flex;
  background: #fff;
}

.tabs button {
  flex: 1;
  padding: 4px 6px;
  font-size: 12px;
  border: none;
  cursor: pointer;
  background-color: #fff;
  font-weight: bold;
  color: #555;
  border-radius: 3px;
  margin: 0 3px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tabs button .icon {
  width: 30px;
  height: 30px;
  margin-right: 4px;
}

.chat-history button .copyicon {
  display: inline;
  width: 8px;
  height: 8px;
  margin-right: 4px;
  align-items: center;
  justify-content: center;
}

.tabs button.active {
  background-color: #f0eaff;
}

.tabs button:hover {
  background-color: #f0eaff;
}

.spinner {
  margin: 10px auto;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
